import { canScore } from '../lib/evaluationIntegrity';
import apiClient from './client';

// #Fase0-B3 — Mapeo pregunta API ⇄ formulario del constructor, en UN solo
// lugar. El formulario usa allows_notes / requires_evidence; la API espera
// notes_enabled / evidence_required. Al cargar se traduce hacia el
// formulario (normQuestion) y al guardar de regreso (toApiQuestion).

// API → formulario. Manda el valor de la API; los alias del formulario
// solo se usan si la API no trae el campo.
const normQuestion = (q) => ({
  ...q,
  allow_na: q.allow_na ?? true,
  type: q.type || q.question_type || 'scale',
  requires_evidence: (q.requirements?.photo || (q.evidence_required || q.requires_evidence ? 'always' : 'never')) === 'always',
  requirements: { ...q.requirements, photo: q.requirements?.photo || (q.evidence_required || q.requires_evidence ? 'always' : 'never') },
  allows_notes: q.notes_enabled ?? q.allows_notes ?? true,
});

const toNumberOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

// Formulario → API. Solo los campos que TemplateQuestionUpdate persiste.
// Antes se mandaba el formulario completo, incluidos campos que la API
// descarta (yes_value / no_value de Sí-No, weight), y el constructor daba
// a entender que se guardaban.
// #Fase1-1 — Los tipos informativos no califican ni bloquean.
export const toApiQuestion = (form = {}) => {
  const question_type = form.question_type || form.type || 'score_scale';
  // #Fase1-1 — La API decide el puntaje de los binarios.
  const scoring_enabled = canScore({ ...form, question_type });
  const payload = {
    code: form.code || null,
    allow_na: form.allow_na ?? true,
    is_critical: !!form.is_critical,
    favorable_answer: form.favorable_answer || null,
    requirements: Object.fromEntries(
      ['observation', 'photo', 'geo', 'document', 'recommendation'].map((k) => [
        k,
        form.requirements?.[k] || (k === 'photo' && (form.requires_evidence || form.evidence_required) ? 'always' : 'never'),
      ])
    ),
    // #Fase3-A3 — No descartar el requisito al guardar la pregunta desde el constructor.
    requires_provider: form.requires_provider || null,
    visible_if: form.visible_if || null,
    title: form.title,
    description: form.description ?? null,
    recinto_seguro: form.recinto_seguro ?? null,
    question_type,
    is_required: (form.is_required ?? true) !== false,
    scoring_enabled,
    is_blocking: scoring_enabled ? !!form.is_blocking : false,
    evidence_required: form.requirements?.photo ? form.requirements.photo === 'always' : !!(form.requires_evidence ?? form.evidence_required),
    notes_enabled: (form.allows_notes ?? form.notes_enabled ?? true) !== false,
    options: (form.options || []).map((o) => ({
      id: o.id,
      label: o.label ?? '',
      value: toNumberOrNull(o.value) ?? 0,
      description: o.description ?? null,
      recommendation: o.recommendation ?? null,
    })),
    min_value: toNumberOrNull(form.min_value),
    max_value: toNumberOrNull(form.max_value),
  };
  if (form.max_score !== undefined && form.max_score !== null) payload.max_score = form.max_score;
  // La API ya persiste placeholder ("Instrucción para el evaluador").
  if (form.placeholder !== undefined) payload.placeholder = form.placeholder || null;
  return payload;
};

// Normalize API section (title → name) so the rest of the frontend uses "name"
const normSection = (s) => ({
  ...s,
  name: s.name || s.title,
  questions: (s.questions || []).map(normQuestion),
});
const normTemplate = (t) => ({
  ...t,
  sections: (t.sections || []).map(normSection),
});

export const templatesAPI = {
  list: () => apiClient.get('/templates')
    .then(r => ({
      ...r,
      data: (Array.isArray(r.data) ? r.data : (r.data.items || [])).map(normTemplate),
    })),
  get: (id) => apiClient.get(`/templates/${id}`)
    .then(r => ({ ...r, data: normTemplate(r.data) })),
  create: (data) => apiClient.post('/templates', data),
  update: (id, data) => apiClient.put(`/templates/${id}`, data),
  // #M9 — activate_template ahora sí lee set_as_default (antes no
  // declaraba body y el flag se descartaba en silencio).
  activate: (id, setDefault) => apiClient.post(`/templates/${id}/activate`, { set_as_default: setDefault }),
  archive: (id) => apiClient.post(`/templates/${id}/archive`),
  // #A16 — el backend expone set-default como PATCH, no POST (405 siempre).
  setDefault: (id) => apiClient.patch(`/templates/${id}/set-default`),
  duplicate: (id) => apiClient.post(`/templates/${id}/duplicate`),
  // #Fase0-B3 / C6 — crea un borrador hijo (version+1, parent_id, mismos ids
  // de secciones/preguntas/opciones). Al activarlo, la API retira al padre.
  newVersion: (id) => apiClient.post(`/templates/${id}/new-version`)
    .then(r => ({ ...r, data: normTemplate(r.data) })),
  // #RecintoSeguro — importa textos de criterios desde el JSON de referencia
  importCriteria: (id, json) => apiClient.post(`/templates/${id}/import-criteria`, json),
  // permanent=true does a hard delete; backend rejects unless the template
  // is already 'archivada' or 'borrador' with no active evaluations.
  delete: (id) => apiClient.delete(`/templates/${id}?permanent=true`),
  // #B2 — getDefaultScaleOptions() apuntaba a una ruta que nunca existió
  // en el backend (colisionaba con GET /templates/{id} → 404) y no tenía
  // ningún caller: el botón "Cargar escala 0-10" de TemplateBuilder usa
  // una constante local, no este endpoint. Se elimina en vez de dejar
  // código muerto apuntando a una ruta rota.

  // Sections
  // #Fase1-1 — weight se conserva al actualizar; null usa el nombre de sección.
  addSection: (tid, name, order = 0) =>
    apiClient.post(`/templates/${tid}/sections`, { title: name, order })
      .then(r => ({ ...r, data: normSection(r.data) })),
  updateSection: (tid, sid, data) => {
    const payload = { ...data };
    if (payload.name && !payload.title) { payload.title = payload.name; }
    return apiClient.put(`/templates/${tid}/sections/${sid}`, payload);
  },
  deleteSection: (tid, sid) => apiClient.delete(`/templates/${tid}/sections/${sid}`),
  // #A14 — el backend expone el reorder como PATCH; con PUT, FastAPI lo
  // enrutaba a PUT /{template_id}/sections/{section_id} con
  // section_id='reorder' → 404 silencioso, el orden nunca se guardaba.
  reorderSections: (tid, ids) => apiClient.patch(`/templates/${tid}/sections/reorder`, { ids }),

  // Questions
  // Backend VALID_QUESTION_TYPES: score_scale, single_choice, multiple_choice,
  // yes_no, text_short, text_long, numeric, date, file_upload.
  // 'scale' is NOT valid → use 'score_scale' as the safe default.
  addQuestion: (tid, sid, data = {}) =>
    apiClient.post(`/templates/${tid}/sections/${sid}/questions`, {
      title: data.title || 'Nueva pregunta',
      question_type: data.type || data.question_type || 'score_scale',
      order: data.order ?? 0,
      ...data,
    }).then(r => ({ ...r, data: normQuestion(r.data) })),
  // Devuelve la pregunta ya normalizada al formato del formulario.
  updateQuestion: (tid, sid, qid, data) =>
    apiClient.put(`/templates/${tid}/sections/${sid}/questions/${qid}`, toApiQuestion(data))
      .then(r => ({ ...r, data: normQuestion(r.data) })),
  deleteQuestion: (tid, sid, qid) => apiClient.delete(`/templates/${tid}/sections/${sid}/questions/${qid}`),
  // #A15 — mismo bug que reorderSections: el backend expone PATCH.
  reorderQuestions: (tid, sid, ids) => apiClient.patch(`/templates/${tid}/sections/${sid}/questions/reorder`, { ids }),
};
